import { ChevronDown, X } from 'lucide-react'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import { Badge } from '../ui/badge'
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '../ui/command'
import { FormLabel } from './form-label'
import { cn } from '@/lib/utils'

export type AutoCompleteOption = {
  value: string
  label: string
  description?: string
  group?: string
}

interface AutoCompleteSelectProps {
  field: any
  options: Array<AutoCompleteOption>
  label?: string | React.ReactNode
  placeholder?: string
  id?: string
  name?: string
  required?: boolean
  multiple?: boolean
  addDialog?: React.ReactElement
  clearable?: boolean
  disabled?: boolean
}

export function AutoCompleteSelect({
  field,
  options,
  label,
  placeholder = 'Select...',
  id,
  name,
  required,
  multiple = false,
  clearable = false,
  disabled = false,
  addDialog: AddDialog,
}: AutoCompleteSelectProps) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const fieldValue = field?.state?.value || (multiple ? [] : '')

  const selected = multiple
    ? options.filter((opt) => fieldValue.includes(opt.value))
    : options.find((opt) => fieldValue === opt.value)

  const handleClearAll = useCallback(() => {
    if (multiple) {
      field.handleChange([])
    } else {
      field.handleChange('')
    }
    setInputValue('')
  }, [field, multiple])

  const handleUnselect = useCallback(
    (option: AutoCompleteOption) => {
      if (multiple) {
        const currentValues = Array.isArray(fieldValue) ? fieldValue : []
        const newValues = currentValues.filter(
          (v: string) => v !== option.value,
        )
        field.handleChange(newValues)
      } else {
        field.handleChange('')
      }
    },
    [field, multiple, fieldValue],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (multiple) {
        if (
          e.key === 'Backspace' &&
          inputValue === '' &&
          selected &&
          Array.isArray(selected) &&
          selected.length > 0
        ) {
          const currentValues = Array.isArray(fieldValue) ? fieldValue : []
          field.handleChange(currentValues.slice(0, -1))
        }
      }
    },
    [selected, inputValue, field, multiple, fieldValue],
  )

  const handleBlur = useCallback(() => {
    // Delay to allow click events to fire first
    setTimeout(() => {
      setOpen(false)
      setInputValue('')
    }, 200)
  }, [])

  const handleSelectOption = useCallback(
    (optionValue: string) => {
      setInputValue('')
      if (multiple) {
        const currentValues = Array.isArray(fieldValue) ? fieldValue : []
        field.handleChange([...currentValues, optionValue])
        // Keep focus and open for multiple selection
        inputRef.current?.focus()
      } else {
        field.handleChange(optionValue)
        setOpen(false)
        // Blur after single selection
        setTimeout(() => {
          inputRef.current?.blur()
        }, 0)
      }
    },
    [field, fieldValue, multiple],
  )

  const filteredOptions = useMemo(
    () =>
      options.filter((opt) => {
        if (multiple) {
          const currentValues = Array.isArray(fieldValue) ? fieldValue : []
          return (
            !currentValues.includes(opt.value) &&
            opt.label.toLowerCase().includes(inputValue.toLowerCase())
          )
        } else {
          return opt.label.toLowerCase().includes(inputValue.toLowerCase())
        }
      }),
    [options, fieldValue, inputValue, multiple],
  )

  const groupedOptions = useMemo(() => {
    const hasGroups = filteredOptions.some((opt) => opt.group)

    if (!hasGroups) {
      return { ungrouped: filteredOptions }
    }

    const grouped = filteredOptions.reduce(
      (acc, option) => {
        const groupKey = option.group || 'Other'
        acc[groupKey] ??= []
        acc[groupKey].push(option)
        return acc
      },
      {} as Record<string, Array<AutoCompleteOption>>,
    )

    // Sort options within each group
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => a.label.localeCompare(b.label))
    })

    return grouped
  }, [filteredOptions])

  return (
    <CommandPrimitive
      shouldFilter={false}
      className="w-full overflow-visible bg-transparent"
    >
      <div className="w-full">
        {label && (
          <FormLabel
            htmlFor={name || id}
            className="mb-1 block text-sm"
            required={required}
          >
            {label}
          </FormLabel>
        )}
        <div className="relative">
          <div
            className={cn(
              'rounded-md border border-input px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
              disabled && 'opacity-50 cursor-not-allowed bg-muted',
            )}
          >
            <div className="flex items-center gap-1">
              <div className="flex flex-wrap gap-1 flex-1">
                {multiple
                  ? Array.isArray(selected) &&
                    selected.map((option: AutoCompleteOption) => (
                      <Badge
                        key={option.value}
                        variant="secondary"
                        className="select-none"
                      >
                        {option.label}
                        <button
                          type="button"
                          className="ml-2 inline-flex items-center justify-center"
                          disabled={disabled}
                          onMouseDown={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (!disabled) handleUnselect(option)
                          }}
                        >
                          <X className="size-3 text-muted-foreground hover:text-foreground cursor-pointer" />
                        </button>
                      </Badge>
                    ))
                  : selected &&
                    !Array.isArray(selected) && (
                      <span className="text-sm">{selected.label}</span>
                    )}
                <input
                  ref={inputRef}
                  onKeyDown={disabled ? undefined : handleKeyDown}
                  value={inputValue}
                  onChange={
                    disabled
                      ? undefined
                      : (e) => {
                          setInputValue(e.target.value)
                          if (!open) setOpen(true)
                        }
                  }
                  onBlur={disabled ? undefined : handleBlur}
                  onFocus={disabled ? undefined : () => setOpen(true)}
                  onClick={disabled ? undefined : () => setOpen(true)}
                  placeholder={!multiple && selected ? '' : placeholder}
                  className="ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground min-w-0"
                  disabled={disabled}
                  readOnly={disabled}
                />
              </div>
              {clearable &&
                (multiple
                  ? Array.isArray(selected) && selected.length > 0
                  : selected) && (
                  <button
                    type="button"
                    className="mr-2 inline-flex items-center justify-center hover:bg-muted rounded-sm p-1"
                    disabled={disabled}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (!disabled) handleClearAll()
                    }}
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer" />
                  </button>
                )}
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform duration-200',
                  open && 'rotate-180',
                  !disabled && 'hover:cursor-pointer',
                )}
                onClick={
                  disabled
                    ? undefined
                    : () => {
                        if (open) {
                          setOpen(false)
                          inputRef.current?.blur()
                        } else {
                          setOpen(true)
                          inputRef.current?.focus()
                        }
                      }
                }
              />
            </div>
          </div>

          {/* Dropdown */}
          <div className="relative mt-1">
            <div
              className={cn(
                'animate-in fade-in-0 zoom-in-95 absolute top-0 z-50 w-full rounded-md bg-popover border shadow-md outline-none',
                open ? 'block' : 'hidden',
              )}
            >
              {AddDialog && (
                <div className="border-b p-1 w-full">
                  <div className="w-full *:w-full">{AddDialog}</div>
                </div>
              )}
              <CommandList
                className={cn(
                  'max-h-75 overflow-y-auto overflow-x-hidden overscroll-contain rounded-md',
                  '[&::-webkit-scrollbar]:w-2',
                  '[&::-webkit-scrollbar-track]:bg-transparent',
                  '[&::-webkit-scrollbar-thumb]:bg-border',
                  '[&::-webkit-scrollbar-thumb]:rounded-full',
                  '[&::-webkit-scrollbar-thumb]:hover:bg-border/80',
                )}
                style={
                  {
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin',
                  } as React.CSSProperties
                }
              >
                {filteredOptions.length > 0 ? (
                  Object.keys(groupedOptions)
                    .sort()
                    .map((groupKey) => (
                      <CommandGroup
                        key={groupKey}
                        heading={
                          groupKey !== 'ungrouped' ? groupKey : undefined
                        }
                      >
                        {groupedOptions[groupKey].map(
                          (option: AutoCompleteOption) => (
                            <CommandItem
                              key={option.value}
                              value={option.label}
                              onMouseDown={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                              }}
                              onSelect={() => handleSelectOption(option.value)}
                              className="cursor-pointer"
                              title={option.description}
                            >
                              <div className="flex flex-col">
                                <span>{option.label}</span>
                                {option.description && (
                                  <span className="text-xs text-neutral-500 whitespace-normal">
                                    {option.description}
                                  </span>
                                )}
                              </div>
                            </CommandItem>
                          ),
                        )}
                      </CommandGroup>
                    ))
                ) : (
                  <CommandEmpty>
                    {inputValue ? 'No results found.' : 'No options available.'}
                  </CommandEmpty>
                )}
              </CommandList>
            </div>
          </div>
        </div>
        {field?.state?.meta?.isTouched && !field?.state?.meta?.isValid ? (
          <p className="text-xs text-red-500">
            {field?.state?.meta?.errors?.join(', ')}
          </p>
        ) : null}
        <p className="text-xs">
          {field?.state?.meta?.isValidating ? 'Validating...' : null}
        </p>
      </div>
    </CommandPrimitive>
  )
}
